/**
 * Quand ce chantier peut commencer, d'après le planning.
 *
 * Le patron, sur le devis : « il ne donne pas la disponibilité, on ne sait pas
 * quand le chantier sera réalisable, il faut prévenir le client d'une date
 * prévisionnelle de début ».
 *
 * ══════════════════════════════════════════════════════════════
 * Ce que ce calcul dit, et ce qu'il ne dit pas
 * ══════════════════════════════════════════════════════════════
 * Il répond à **« à partir de quand ai-je du monde »**, et à rien d'autre. Il
 * ne dit pas que le chantier sera fini à la date de fin : celle-ci découle de
 * la durée estimée du devis, qui est une estimation du patron et non une
 * mesure. La nuance n'est pas rhétorique — c'est elle qui décide de ce qu'on
 * écrit à l'écran, et donc de ce que le client comprendra.
 *
 * D'où deux partis pris :
 *
 *   · **le nombre de personnes requis est une saisie, pas une déduction.**
 *     Un devis ne dit nulle part s'il faut un homme ou quatre. Le supposer
 *     produirait une date fausse dans un cas sur deux, et personne ne saurait
 *     lequel. Le patron pose le chiffre ; le calcul répond pour ce chiffre-là ;
 *   · **aucune date n'est rendue quand il n'y en a pas.** Équipe vide, effectif
 *     demandé plus grand que l'équipe, horizon épuisé : la réponse est nulle et
 *     dit pourquoi, plutôt que de proposer un lundi au hasard.
 *
 * Le week-end ne compte pas, comme dans `computeWeekLoad` et pour la même
 * raison : une affectation longue le couvre par construction. Les jours fériés
 * ne sont pas connus du produit — c'est une limite assumée, écrite ici pour
 * qu'elle ne se redécouvre pas un 15 août.
 */
import type { Employee } from "../team/types.js";
import type { Assignment } from "../field/types.js";
import { coversDay, isActiveOn } from "../field/types.js";
import { blocksPlanning } from "./types.js";
import type { Absence } from "./types.js";

const DAY_MS = 86_400_000;

const parse = (iso: string) => Date.parse(`${iso}T00:00:00Z`);
const toIso = (t: number) => new Date(t).toISOString().slice(0, 10);

/** Samedi et dimanche. `getUTCDay()` rend 0 le dimanche et 6 le samedi. */
function estOuvre(iso: string): boolean {
  const j = new Date(parse(iso)).getUTCDay();
  return j !== 0 && j !== 6;
}

/** Les `count` jours ouvrés à partir de `from`, celui-ci compris s'il l'est. */
function joursOuvres(from: string, count: number): string[] {
  const jours: string[] = [];
  let t = parse(from);
  // Borne dure : 7/5 de jours calendaires suffisent, plus une marge pour le
  // week-end sur lequel on peut tomber en partant. Sans elle, une entrée
  // absurde ferait tourner la boucle indéfiniment.
  const plafond = count * 2 + 14;
  for (let i = 0; jours.length < count && i < plafond; i++, t += DAY_MS) {
    const iso = toIso(t);
    if (estOuvre(iso)) jours.push(iso);
  }
  return jours;
}

export type AvailabilityVerdict = "trouvee" | "sans_equipe" | "horizon_depasse";

export interface AvailabilityInput {
  /** Premier jour envisageable, inclus. En pratique : aujourd'hui. */
  from: string;
  employees: readonly Employee[];
  assignments: readonly Assignment[];
  absences: readonly Absence[];
  /** La durée estimée du devis, en jours ouvrés. `null` si le devis n'en porte pas. */
  durationDays: number | null;
  /** Combien de personnes il faut sur place. Saisi par le patron, jamais déduit. */
  crewSize: number;
  /**
   * Combien de jours ouvrés de **début** on essaie. Trois mois par défaut.
   *
   * Ce n'est pas la profondeur du calcul : un chantier de six mois qui
   * commence dans deux semaines est examiné jusqu'à sa fin, bien au-delà de
   * l'horizon. L'horizon borne la question « à partir de quand », pas la
   * durée du chantier.
   */
  horizonDays?: number;
}

export interface Availability {
  /** Premier jour ouvré où l'effectif demandé est libre. `null` s'il n'y en a pas. */
  startsOn: string | null;
  /** Dernier jour ouvré de la fenêtre. `null` quand la durée n'est pas estimée. */
  endsOn: string | null;
  crewSize: number;
  /** Le minimum de personnes libres sur la fenêtre retenue. */
  freeOnWindow: number;
  verdict: AvailabilityVerdict;
  /** Phrase à afficher telle quelle. Jamais vide. */
  explanation: string;
}

const HORIZON_PAR_DEFAUT = 60;

/**
 * La première fenêtre où l'effectif demandé est libre.
 *
 * Une personne est libre un jour donné si **aucune affectation ne la couvre**
 * et **aucune absence accordée** non plus. Une demande de congé en attente ne
 * compte pas : elle n'est pas acquise, et la traiter comme telle avancerait la
 * date de début au bénéfice d'un congé que le patron n'a pas encore accordé.
 */
export function computeAvailability(input: AvailabilityInput): Availability {
  const crewSize = Math.min(50, Math.max(1, Math.round(input.crewSize)));
  const horizon = input.horizonDays ?? HORIZON_PAR_DEFAUT;
  const actifs = input.employees.filter((e) => e.isActive);

  if (actifs.length === 0) {
    return sansDate(crewSize, "sans_equipe", "Aucun salarié actif : le planning ne peut rien proposer.");
  }

  if (crewSize > actifs.length) {
    return sansDate(
      crewSize,
      "sans_equipe",
      `L'entreprise compte ${actifs.length} salarié${actifs.length > 1 ? "s" : ""} actif${
        actifs.length > 1 ? "s" : ""
      } : ${crewSize} personnes ne seront jamais libres en même temps.`,
    );
  }

  // Une durée nulle ou absente ramène la fenêtre à un seul jour : on répond
  // alors « à partir de quand », sans prétendre savoir jusqu'à quand.
  // Borne haute : le schema du service plafonne deja la duree a 365 jours,
  // mais la fonction est exportee et un appelant direct perdrait ce garde-fou.
  // `largeur` dimensionne un tableau puis un `Math.min(...)` par etalement :
  // une valeur absurde ferait tourner la boucle longtemps avant d'echouer.
  const largeur = Math.min(365, Math.max(1, Math.round(input.durationDays ?? 1)));

  // `horizon + largeur` et non `horizon` : trouvé à l'écran le 12 septembre.
  // Un chantier de 180 jours estimés dans un horizon de 60 ne laissait
  // **aucune fenêtre complète** à examiner, et la fonction répondait « aucune
  // fenêtre de 180 jours dans les 60 prochains jours ouvrés » — vrai, inutile,
  // et taisant la seule vraie raison : on n'avait pas regardé assez loin. Or
  // les chantiers longs sont le cas courant du bâtiment.
  const jours = joursOuvres(input.from, horizon + largeur);

  const libresParJour = jours.map(
    (jour) =>
      actifs.filter(
        (e) =>
          !input.assignments.some((a) => a.employeeId === e.id && coversDay(a, jour)) &&
          // Les affectations passent par `coversDay`, qui ajoute la règle du
          // week-end ; les absences par `isActiveOn`, qui ne répond qu'à
          // « ce jour tombe-t-il dans la période ». Une seconde implémentation
          // de cette question aurait fini par diverger sur l'inclusion des
          // bornes.
          !input.absences.some(
            (ab) => ab.employeeId === e.id && blocksPlanning(ab) && isActiveOn(ab, jour),
          ),
      ).length,
  );

  // Les débuts essayés restent bornés à l'horizon ; la fenêtre, elle, peut
  // s'étendre au-delà. Promettre un démarrage dans dix mois n'engagerait
  // personne — le planning de juillet n'existe pas en septembre.
  for (let debut = 0; debut < horizon && debut + largeur <= jours.length; debut++) {
    const fenetre = libresParJour.slice(debut, debut + largeur);
    const minimum = Math.min(...fenetre);
    if (minimum < crewSize) continue;

    const startsOn = jours[debut]!;
    const endsOn = input.durationDays ? jours[debut + largeur - 1]! : null;

    return {
      startsOn,
      endsOn,
      crewSize,
      freeOnWindow: minimum,
      verdict: "trouvee",
      explanation: input.durationDays
        ? `${minimum} personne${minimum > 1 ? "s" : ""} libre${minimum > 1 ? "s" : ""} sur ` +
          `les ${largeur} jours ouvrés à partir de cette date.`
        : `${minimum} personne${minimum > 1 ? "s" : ""} libre${minimum > 1 ? "s" : ""} ce jour-là. ` +
          `Renseignez la durée estimée du devis pour obtenir aussi une date de fin.`,
    };
  }

  return sansDate(
    crewSize,
    "horizon_depasse",
    `Personne n'est libre assez longtemps : il faudrait ${largeur} jour${
      largeur > 1 ? "s" : ""
    } ouvré${largeur > 1 ? "s" : ""} d'affilée avec ${crewSize} personne${
      crewSize > 1 ? "s" : ""
    }, et aucun créneau ne s'ouvre dans les ${horizon} prochains jours ouvrés.`,
  );
}

function sansDate(
  crewSize: number,
  verdict: AvailabilityVerdict,
  explanation: string,
): Availability {
  return { startsOn: null, endsOn: null, crewSize, freeOnWindow: 0, verdict, explanation };
}
